import type { FormMutation, FormValues } from '@workwave-tidal/core/form'
import { useEffect } from 'react'

import { form } from '../formState'
import type { OrderFormValues } from '../typings'

const MUTATION_ID = 'carry-location-on-type-change'

function activeStep(type: uui.domain.client.rm.OrderIdentifier): 'pickup' | 'delivery' | null {
  if (type === 'p') return 'pickup'
  if (type === 'd' || type === 's') return 'delivery'
  // 'pd' renders both pickup and delivery fields independently
  return null
}

const carryLocationOnTypeChange: FormMutation<OrderFormValues> = (api, changed) => {
  const nextType = changed.type
  if (!nextType) return

  const prevType = api.getField('type')?.value
  if (!prevType) return

  const from = activeStep(prevType)
  if (!from) return

  // Read the source step from the values tree, NOT from `api.getField(from)`.
  // Picking a location writes the leaf field (`${from}.location`) via the
  // LocationEditor's onChange, which updates the deep values tree but leaves the
  // parent field (`${from}`) entry stale at its registered default. Reading the
  // parent field would therefore carry the default location instead of the one
  // the user picked. `getValues()` always reflects the latest committed values.
  const sourceStep = api.getValues()[from]
  if (!sourceStep) return

  // Nothing meaningful to carry without a source location. Bail out so we never
  // overwrite the target step's location with `undefined` — LocationEditorField's
  // pin initialization throws when connected to a field with no location value.
  if (!sourceStep.location) return

  // When switching to 'pd' both a pickup and a delivery step are shown. The
  // previously active step keeps its location, and the newly added step (the
  // opposite one) must inherit the same location/depot - otherwise it falls back
  // to its default (the current map center), so only one of the two steps shows
  // the address/depot the user selected. For single-step transitions activeStep
  // resolves the target directly.
  const to = nextType === 'pd' ? (from === 'pickup' ? 'delivery' : 'pickup') : activeStep(nextType)
  if (!to || to === from) return

  const stepType: uui.domain.client.rm.OrderStepIdentifier =
    to === 'pickup' ? 'p' : nextType === 's' ? 's' : 'd'

  const carried = {
    ...sourceStep,
    location: { ...sourceStep.location },
    type: stepType,
  }

  const leaves = Object.fromEntries(
    Object.entries(carried).map(([key, value]) => [`${to}.${key}`, value]),
  )

  return { [to]: carried, ...leaves } as unknown as Partial<FormValues<OrderFormValues>>
}

export function useCarryLocationOnTypeChange() {
  const formApi = form.useFormApi()

  useEffect(() => {
    formApi.addFormMutation(MUTATION_ID, carryLocationOnTypeChange)

    return () => void formApi.removeFormMutation(MUTATION_ID)
  }, [formApi])
}
